import { useState, useCallback, useEffect } from "react"
import { useNavigate, useLocation } from 'react-router-dom'
import Calendar from '../components/calendar/Calendar'
import DayEventsList from '../components/calendar/DayEventsList'
import EventModal from '../components/calendar/EventModal'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { useCalendar } from '../hooks/useCalendar'
import { useSyncCalendar } from '../hooks/useSyncCalendar'
import CombinedModal from '../components/calendar/CombinedModal'
import { useEventsStore } from '../stores/eventsStore'
import Layout from '../components/layout/Layout'
import { useCalendarStore } from '../stores/calendarStore'
import { useFamilyStore } from '../stores/familyStore'
import { usePermissions } from '../hooks/usePermissions'
import { format } from 'date-fns'
import logger from '../utils/logger'
import UsageInfoModal from '../components/calendar/UsageInfoModal'
import { useEntitlements } from '../hooks/useEntitlements'
import { useRecentChanges } from '../hooks/useRecentChanges'
import RecentChangesToast from '../components/calendar/RecentChangesToast'
import { InviteSecondParentCard } from '../components/family/InviteSecondParentCard'
import { toast } from 'sonner'

export default function CalendarPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentView] = useState<"care" | "events">("care")
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showCombinedModal, setShowCombinedModal] = useState(false)
  const [showUsageInfo, setShowUsageInfo] = useState(false)
  const [modalDefaultDate, setModalDefaultDate] = useState<Date>(new Date())
  
  const isOnline = useOnlineStatus()
  const { family, isLoading: familyLoading, initialized: familyInitialized } = useFamilyStore()
  // Użyj rzeczywistej liczby dzieci z familyStore (cache + realne dane), aby uniknąć wyścigu z hookiem kalendarza
  const realChildrenCount = useFamilyStore(state => state.children.length)
  const { userRole, canEditCalendar } = usePermissions()
  
  const { 
    children, 
    selectedChildId, 
    selectedHome,
    setSelectedChild,
    setSelectedHome,
    updateCalendarEntry,
    isLoading,
    error,
    currentDate,
    entries
  } = useCalendar()
  
  // Inicjalizuj synchronizację kalendarza
  useSyncCalendar()

  // Recent changes notification (Pro/Max only)
  const { plan: entitlements, planId } = useEntitlements(family?.id)
  const recentChanges = useRecentChanges()

  useEffect(() => {
    if (!entitlements.recentChangesEnabled) return
    if (!familyInitialized || !family) return
    if (recentChanges.isLoading || !recentChanges.hasChanges) return

    // Don't show again in this session for the same batch
    const sessionKey = `changes-toast-shown-${family.id}`
    if (sessionStorage.getItem(sessionKey)) return

    const timer = setTimeout(() => {
      sessionStorage.setItem(sessionKey, '1')
      toast.custom(
        (t) => (
          <RecentChangesToast
            notes={recentChanges.notes}
            expenses={recentChanges.expenses}
            careChangesCount={recentChanges.careChangesCount}
            onDismiss={() => {
              recentChanges.markAsSeen()
              toast.dismiss(t)
            }}
          />
        ),
        { duration: Infinity, id: 'recent-changes' }
      )
    }, 500)

    return () => clearTimeout(timer)
  }, [entitlements.recentChangesEnabled, familyInitialized, family, recentChanges.isLoading, recentChanges.hasChanges])

  // Re-check on visibility change (app resume from background)
  useEffect(() => {
    if (!entitlements.recentChangesEnabled || !family) return

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Clear session flag so new changes can trigger toast
        sessionStorage.removeItem(`changes-toast-shown-${family.id}`)
        recentChanges.refetch()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [entitlements.recentChangesEnabled, family?.id])

  // Udostępnij globalnie handler otwierający UsageInfoModal dla ikonki "i" w Navbar
  useEffect(() => {
    ;(window as any).__openUsageInfo = () => setShowUsageInfo(true)
    return () => { try { delete (window as any).__openUsageInfo } catch {} }
  }, [])

  // Inicjalizuj dom na podstawie roli użytkownika
  useEffect(() => {
    if (selectedHome === null && userRole) {
      // Auto-wybór domu na podstawie roli użytkownika
      const defaultHome = userRole === 'parent_a' ? 'home_a' : 
                         userRole === 'parent_b' ? 'home_b' : 'home_a'
      logger.debug(`🏠 Auto-wybór domu dla roli ${userRole}: ${defaultHome}`)
      setSelectedHome(defaultHome)
    }
  }, [selectedHome, userRole, setSelectedHome])

  // Jeśli jest rodzina, ale brak realnych dzieci – przekieruj do ustawień
  useEffect(() => {
    if (familyInitialized && !familyLoading && family && realChildrenCount === 0) {
      navigate('/family-settings')
    }
  }, [familyInitialized, familyLoading, family, realChildrenCount, navigate])

  // Jednorazowe przejście z ustawień ogólnych do kalendarza nowo dodanego dziecka
  useEffect(() => {
    const state = location.state as { focusChildId?: string } | null
    if (!state?.focusChildId) return

    const focusChildId = state.focusChildId
    const hasChild = children.some((child) => child.id === focusChildId)

    if (!hasChild) {
      // Poczekaj, aż lista dzieci zaktualizuje się z backendu
      return
    }

    logger.debug(`👶 Otrzymano focusChildId=${focusChildId} z nawigacji – ustawiam jako wybrane dziecko`)
    setSelectedChild(focusChildId)

    // Wyczyść state nawigacji, żeby przekierowanie było jednorazowe
    navigate(location.pathname, { replace: true })
  }, [location, children, setSelectedChild, navigate])

  // Prosta zmiana widoku bez animacji – tylko zmiana trybu interakcji
  // Przełącznik widoków nieużywany – pozostawiamy pojedynczy widok Opieka

  const handleHomeChange = useCallback((home: "home_a" | "home_b") => {
    setSelectedHome(home)
  }, [setSelectedHome])
  
  const handleChildChange = useCallback((childId: string) => {
    setSelectedChild(childId)
  }, [setSelectedChild])
  
  const handleCareDayClick = useCallback((date: Date) => {
    setSelectedDate(date)
    const dateString = format(date, 'yyyy-MM-dd')
    const entries = useCalendarStore.getState().entries
    const dayEntries = entries.filter(entry => entry.date === dateString)
    const dayEntry = dayEntries[0]
    const currentLocation = dayEntry?.location || null
    // Sprawdź, czy istnieją wydarzenia (notatki/koszty) w tym dniu
    try {
      const events = useEventsStore.getState().events
      const hasEvents = events.some(ev => ev.child_id === (selectedChildId || '') && format(new Date(ev.start_at), 'yyyy-MM-dd') === dateString)
      if (hasEvents) {
        // Jeżeli są wydarzenia – tylko zaznacz
        return
      }
    } catch {}
    // 1) Pusta komórka → przypisz wybrany dom (jak dotąd)
    if (!currentLocation) {
      if (!canEditCalendar) return
      if (selectedHome !== undefined && selectedHome !== null) {
        updateCalendarEntry(dateString, "day", selectedHome)
      }
      return
    }
    // 2) Zajęta komórka (opieka/notatka/koszt) → tylko zaznacz
  }, [selectedHome, selectedChildId, updateCalendarEntry, canEditCalendar])

  const handleEventsDayClick = useCallback((date: Date) => {
    setSelectedDate(date)
  }, [])

  const handleDayDoubleClick = useCallback((date: Date) => {
    setSelectedDate(date)
    setModalDefaultDate(date)
    setShowCombinedModal(true)
  }, [])

  

  // Jeśli familyStore się jeszcze ładuje, pokaż loading
  if (!familyInitialized || familyLoading) {
    return (
      <Layout 
        currentHome="home_a"
        onHomeChange={() => {}}
        selectedChild=""
        onChildChange={() => {}}
        childrenData={[]}
        isLoading={true}
        currentView={currentView}
        onViewChange={() => {}}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Sprawdzanie członkostwa w rodzinie...</p>
          </div>
        </div>
      </Layout>
    )
  }

  // Jeśli użytkownik nie ma rodziny (po pełnej inicjalizacji), pokaż informację i link do tworzenia rodziny
  // WAŻNE: Sprawdzamy tylko familyStore, nie isLoading z useCalendar
  if (!family && familyInitialized && !familyLoading) {
    return (
      <Layout 
        currentHome="home_a"
        onHomeChange={() => {}}
        selectedChild=""
        onChildChange={() => {}}
        childrenData={[]}
        isLoading={false}
        currentView={currentView}
        onViewChange={() => {}}
      >
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Witaj w 2domy.pl!
              </h2>
              <p className="text-gray-600">
                Aby rozpocząć korzystanie z kalendarza, utwórz rodzinę lub poproś o zaproszenie do istniejącej.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/family-settings'}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full"
              >
                Utwórz nową rodzinę
              </button>
              <p className="text-sm text-gray-500">
                Lub poproś członka rodziny o wysłanie zaproszenia na Twój adres email
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout 
      currentHome={selectedHome || "home_a"}
      onHomeChange={handleHomeChange}
      selectedChild={selectedChildId || ""}
      onChildChange={handleChildChange}
      childrenData={children}
      isLoading={isLoading}
      currentView={currentView}
      onViewChange={undefined}
      onAddEventClick={() => {
        const d = selectedDate || currentDate
        setSelectedDate(d)
        setModalDefaultDate(d)
        setShowCombinedModal(true)
      }}
    >
      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 text-center">
          <span className="text-sm font-medium">
            Pracujesz w trybie offline. Zmiany zostaną zsynchronizowane po połączeniu z internetem.
          </span>
        </div>
      )}
      
      {/* Error banner */}
      {error && (
        <div className="bg-gradient-to-r from-red-400 to-pink-400 text-white px-4 py-2 text-center">
          <span className="text-sm font-medium">
            Błąd: {error}
          </span>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-0 sm:px-4 lg:px-8 py-1 sm:py-3">
        <InviteSecondParentCard isPaying={planId !== 'basic'} />
        {/* Kalendarz na pełną szerokość */}
        <div className="calendar-full-width">
          {(isLoading && entries.length === 0) ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-slate-600">Ładowanie kalendarza...</p>
              </div>
            </div>
          ) : (
            <Calendar 
              view="care"
              selectedChild={selectedChildId || ""}
              selectedDate={selectedDate || currentDate}
              onDayClick={currentView === 'events' ? handleEventsDayClick : handleCareDayClick}
              onDayDoubleClick={handleDayDoubleClick}
            />
          )}
        </div>
      </div>

      {/* Lista wydarzeń dnia pod kalendarzem (dla obu trybów) */}
      {selectedChildId && selectedChildId !== 'all-children' && (
        <div className="max-w-[1600px] mx-auto px-0 sm:px-4 lg:px-8 pb-4">
          <DayEventsList
            date={selectedDate || currentDate}
            childId={selectedChildId}
            onAddEvent={(d) => { setModalDefaultDate(d); setSelectedDate(d); setSelectedEventId(null); setShowEventModal(true) }}
            onEditEvent={(eventId, d) => { setModalDefaultDate(d); setSelectedDate(d); setSelectedEventId(eventId); setShowEventModal(true) }}
          />
        </div>
      )}
      
      {/* CombinedModal – Opieka/Notatka/Koszt podwójnym kliknięciem */}
      {selectedChildId && selectedChildId !== 'all-children' && selectedDate && (
        <CombinedModal
          isOpen={showCombinedModal}
          onClose={() => setShowCombinedModal(false)}
          defaultDate={selectedDate}
          childId={selectedChildId}
        />
      )}

      {/* Usage Info Modal */}
      <UsageInfoModal isOpen={showUsageInfo} onClose={() => setShowUsageInfo(false)} />

      {/* Modal wydarzenia */}
      {selectedChildId && selectedChildId !== 'all-children' && (
        <EventModal
          isOpen={showEventModal}
          onClose={() => { setShowEventModal(false); setSelectedEventId(null) }}
          defaultDate={modalDefaultDate}
          childId={selectedChildId}
          eventId={selectedEventId || undefined}
        />
      )}
    </Layout>
  )
} 